<template>
  <div>
    <BenefitsSidePanel
      v-model="panelOpen"
      :title="title"
      :close-label="t('benefitsCenter-tiers-panel-close')"
      :before-close="confirmClose"
      width="700px"
      @close="handleClose"
    >
      <div class="benefits-tier-panel">
        <KvTabs v-model="activeSection" grow>
          <template #tabs>
            <KvTab value="settings">{{ t("benefitsCenter-tiers-panel-settings") }}</KvTab>
            <KvTab value="design">{{ t("benefitsCenter-tiers-panel-design") }}</KvTab>
          </template>
        </KvTabs>

        <BenefitsTierSettingsForm
          v-if="activeSection === 'settings'"
          v-model="draft"
          :tiers="tiers"
          :segments="segments"
          :segments-loading="segmentsLoading"
          :rewards="rewards"
        />

        <BenefitsTierDesignForm v-else v-model="draft" />
      </div>

      <template slot="footer">
        <div class="benefits-tier-panel-footer">
          <KvButton class="benefits-tier-panel-cancel" type="button" variant="outlined" @click="requestClose">
            {{ t("benefitsCenter-tiers-panel-close") }}
          </KvButton>

          <div class="benefits-tier-panel-footer-right">
            <KvButton
              v-if="draft.id"
              class="benefits-tier-panel-delete"
              type="button"
              prepend-icon="ui-trash-01"
              :disabled="saving"
              @click="$emit('delete', tier)"
            >
              {{ t("benefitsCenter-tiers-panel-delete") }}
            </KvButton>

            <KvTooltip :text="missingFieldsTooltip" :disabled="canSave" placement="top">
              <KvButton
                class="benefits-tier-panel-save"
                type="button"
                :prepend-icon="draft.id ? 'ui-check' : 'ui-plus'"
                :disabled="!canSave"
                :loading="saving"
                @click="save"
              >
                {{ draft.id ? t("benefitsCenter-tiers-panel-saveChanges") : t("benefitsCenter-tiers-panel-save") }}
              </KvButton>
            </KvTooltip>
          </div>
        </div>
      </template>
    </BenefitsSidePanel>

    <BenefitsConfirmModal
      v-model="discardConfirmOpen"
      :title="t('benefitsCenter-tiers-unsaved-title')"
      :description="t('benefitsCenter-tiers-unsaved-description')"
      :cancel-label="t('benefitsCenter-tiers-unsaved-keepEditing')"
      :confirm-label="t('benefitsCenter-tiers-unsaved-discard')"
      @confirm="discardChanges"
    />
  </div>
</template>

<script>
import keviApi from "../../../keviApi";
import KvTabs from "../../../components/tabs/kv-tabs.vue";
import KvTab from "../../../components/tabs/kv-tab.vue";
import KvTooltip from "../../../components/ui/kv-tooltip.vue";
import BenefitsSidePanel from "../../shared/benefits-side-panel.vue";
import BenefitsConfirmModal from "../../shared/benefits-confirm-modal.vue";
import BenefitsTierSettingsForm from "./benefits-tier-settings-form.vue";
import BenefitsTierDesignForm from "./benefits-tier-design-form.vue";
import { htmlToText } from "../../../helpers/html";

const DEFAULT_COLOR = "#B45F24";
const DEFAULT_TEXT_COLOR = "#FCFCFD";

export default {
  name: "BenefitsTierPanel",
  components: {
    KvTabs,
    KvTab,
    KvTooltip,
    BenefitsSidePanel,
    BenefitsConfirmModal,
    BenefitsTierSettingsForm,
    BenefitsTierDesignForm,
  },
  model: {
    prop: "value",
    event: "input",
  },
  props: {
    value: { type: Boolean, default: false },
    tier: { type: Object, default: null },
    tiers: { type: Array, default: () => [] },
    rewards: { type: Array, default: () => [] },
    saving: { type: Boolean, default: false },
  },
  data() {
    return {
      panelOpen: this.value,
      activeSection: "settings",
      draft: this.createDraft(this.tier),
      snapshot: "",
      discardConfirmOpen: false,
      submitting: false,
      segments: [],
      segmentsLoading: false,
    };
  },
  computed: {
    title() {
      return this.t(this.draft.id ? "benefitsCenter-tiers-panel-editTitle" : "benefitsCenter-tiers-panel-createTitle");
    },
    missingFields() {
      const missing = [];

      if (!this.draft.name.trim()) missing.push(this.t("benefitsCenter-tiers-panel-name"));
      if (!htmlToText(this.draft.description)) missing.push(this.t("benefitsCenter-tiers-panel-description"));
      if (!this.draft.segmentId) missing.push(this.t("benefitsCenter-tiers-panel-segment"));
      if (!htmlToText(this.draft.becomingDescription)) missing.push(this.t("benefitsCenter-tiers-panel-becoming"));
      if (!this.draft.color) missing.push(this.t("benefitsCenter-tiers-panel-tierColor"));
      if (!this.draft.textColor) missing.push(this.t("benefitsCenter-tiers-panel-textColor"));

      return missing;
    },
    canSave() {
      return this.missingFields.length === 0;
    },
    missingFieldsTooltip() {
      if (this.canSave) return "";
      return this.t("benefitsCenter-tiers-panel-missingFieldsTooltip", { fields: this.missingFields.join(", ") });
    },
    isDirty() {
      return this.fingerprint(this.draft) !== this.snapshot;
    },
  },
  watch: {
    value(isOpen) {
      this.panelOpen = isOpen;
      if (isOpen) {
        this.reset();
        this.loadSegments();
      }
    },
    panelOpen(isOpen) {
      this.$emit("input", isOpen);
    },
    saving(isSaving) {
      if (!isSaving) {
        this.submitting = false;
      }
    },
  },
  methods: {
    t(term, replaces) {
      return this.$root.t(term, replaces);
    },
    createDraft(tier) {
      const safeTier = tier || {};

      return {
        id: safeTier.id || "",
        name: safeTier.name || "",
        description: safeTier.description || "",
        segmentId: safeTier.segmentId ? String(safeTier.segmentId) : "",
        becomingDescription: safeTier.becomingDescription || "",
        color: safeTier.color || DEFAULT_COLOR,
        textColor: safeTier.textColor || DEFAULT_TEXT_COLOR,
        iconType: safeTier.iconType === "svg" ? "svg" : "emoji",
        iconEmoji: safeTier.iconEmoji || "",
        iconSvg: safeTier.iconSvg || "",
        iconSvgName: safeTier.iconSvgName || "",
      };
    },
    reset() {
      this.activeSection = "settings";
      this.draft = this.createDraft(this.tier);
      this.snapshot = this.fingerprint(this.draft);
      this.discardConfirmOpen = false;
      this.submitting = false;
    },
    fingerprint(draft) {
      return JSON.stringify({
        ...draft,
        description: htmlToText(draft.description),
        becomingDescription: htmlToText(draft.becomingDescription),
      });
    },
    loadSegments() {
      if (this.segmentsLoading) return;

      this.segmentsLoading = true;
      this.$http(keviApi.getOptions("segments"))
        .then((response) => {
          const segments = Array.isArray(response.body) ? response.body : [];
          this.segments = segments.map((segment) => ({ id: String(segment.id), name: segment.name }));
        })
        .catch(() => {
          this.$store.dispatch("notification/error", {
            text: this.t("benefitsCenter-tiers-panel-segmentLoadError"),
          });
        })
        .finally(() => {
          this.segmentsLoading = false;
        });
    },
    confirmClose() {
      if (this.saving) return false;
      if (!this.isDirty) return true;

      this.discardConfirmOpen = true;
      return false;
    },
    requestClose() {
      if (this.confirmClose()) {
        this.panelOpen = false;
        this.handleClose();
      }
    },
    discardChanges() {
      this.discardConfirmOpen = false;
      this.panelOpen = false;
      this.handleClose();
    },
    handleClose() {
      this.$emit("close");
    },
    save() {
      if (!this.canSave || this.saving || this.submitting) return;

      this.submitting = true;

      const isEmoji = this.draft.iconType === "emoji" && !!this.draft.iconEmoji;
      const isSvg = this.draft.iconType === "svg" && !!this.draft.iconSvg;

      this.$emit("save", {
        ...this.draft,
        name: this.draft.name.trim(),
        iconType: isEmoji || isSvg ? this.draft.iconType : null,
        iconEmoji: isEmoji ? this.draft.iconEmoji : null,
        iconSvg: isSvg ? this.draft.iconSvg : null,
        iconSvgName: isSvg ? this.draft.iconSvgName || null : null,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.benefits-tier-panel {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.benefits-tier-panel-footer {
  align-items: center;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  width: 100%;
}

.benefits-tier-panel-footer-right {
  align-items: center;
  display: flex;
  gap: 12px;
}

.benefits-tier-panel-cancel {
  color: #1f2a37 !important;
}

.benefits-tier-panel-save {
  background-color: #2aaa4c !important;
  border-color: #2aaa4c !important;
  color: #ffffff !important;
  min-width: 140px;
}

.benefits-tier-panel-delete {
  background-color: #fee4e2 !important;
  border-color: #fee4e2 !important;
  color: #f04438 !important;

  &:hover:not(:disabled) {
    background-color: #fcd5d3 !important;
  }
}

@media (max-width: 767px) {
  .benefits-tier-panel-save {
    min-width: 0;
  }
}
</style>
